import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";
import Roboto from "../../../assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoBold from "../../../assets/fonts/Roboto/Roboto-Bold.ttf";

Font.register({
  family: "Roboto",
  fonts: [{ src: Roboto }, { src: RobotoBold, fontWeight: 600 }],
  format: "truetype",
});

// Keep alphanumeric values such as account and ESI numbers on one line.
Font.registerHyphenationCallback((word) => [word]);

const BORDER = "0.8px solid #000";

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: "Roboto",
    fontSize: 8.5,
    color: "#000",
  },
  center: { textAlign: "center" },
  bold: { fontWeight: 600 },
  title: { fontSize: 10, fontWeight: 600, textAlign: "center", marginTop: 1 },
  identity: {
    flexDirection: "row",
    marginTop: 18,
    marginBottom: 10,
  },
  identityColumn: { width: "50%" },
  identityRow: { flexDirection: "row", marginBottom: 4 },
  identityLabel: { width: 90 },
  identityColon: { width: 10 },
  identityValue: { flex: 1 },
  box: { border: BORDER },
  head: {
    flexDirection: "row",
    borderBottom: BORDER,
    fontWeight: 600,
  },
  headCell: { padding: 3, textAlign: "center" },
  cell: { padding: 3 },
  cellRight: { padding: 3, textAlign: "right" },
  cellCenter: { padding: 3, textAlign: "center" },
  body: { flexDirection: "row", minHeight: 220 },
  column: { flexDirection: "column" },
  colBorder: { borderRight: BORDER },
  totalRow: {
    flexDirection: "row",
    borderTop: BORDER,
    fontWeight: 600,
  },
  netRow: {
    borderTop: BORDER,
    padding: 4,
    fontWeight: 600,
  },
  signatureRow: {
    padding: 4,
    alignItems: "flex-end",
  },
  leaveBox: {
    border: BORDER,
    marginTop: 10,
    padding: 6,
    minHeight: 60,
  },
});

// Column widths (percent) for the components table. Earnings and deductions
// share one row so each deduction sits beside the earnings line it belongs to.
const COLS = {
  component: "27%",
  fixed: "16%",
  earning: "16%",
  deductionName: "23%",
  deductionAmount: "18%",
};

const isEmpty = (value) =>
  value === undefined || value === null || value === "";

const toNumber = (value) => {
  if (isEmpty(value)) return 0;
  const parsed = parseFloat(`${value}`.replace(/,/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatMoney = (value) =>
  isEmpty(value)
    ? ""
    : toNumber(value).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

const formatText = (value) => (isEmpty(value) ? "" : `${value}`);

const formatDate = (value) => {
  if (isEmpty(value)) return "";
  const parsed = moment(
    value,
    ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "YYYY/MM/DD", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("DD-MM-YYYY") : `${value}`;
};

const getName = (item) => item?.component_name ?? item?.name ?? "";
const getAmount = (item) => item?.amount ?? item?.value ?? item?.total;
const normalizeKey = (value) => `${value || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");

const pickSection = (...aliases) =>
  aliases.find((alias) => Array.isArray(alias) && alias.length) ?? [];

const sumAmounts = (rows) =>
  rows.reduce((sum, item) => sum + toNumber(getAmount(item)), 0);

// Pay Day and Present Day follow the same payrun-basis rule as the other
// templates: an Hours-basis payrun reports days under total_payable_days.
const getPayableDays = (payslipData) =>
  payslipData?.payrun_basis === "Hours"
    ? payslipData?.total_payable_days
    : payslipData?.net_payable_shift;

// leave_balance_details isn't confirmed against a live response, so each
// entry is matched by its leave type name and its totals are read through a
// list of likely field-name aliases rather than one fixed key.
const findLeaveType = (list, keyword) =>
  (Array.isArray(list) ? list : []).find((item) =>
    `${item?.leave_type_name ?? item?.leave_type ?? item?.name ?? ""}`
      .toLowerCase()
      .includes(keyword),
  );

const leaveField = (entry, ...keys) => {
  for (const key of keys) {
    if (!isEmpty(entry?.[key])) return entry[key];
  }
  return undefined;
};

const leaveValue = (entry, ...keys) => formatText(leaveField(entry, ...keys)) || "00";

const IdentityLine = ({ label, value }) => (
  <View style={styles.identityRow}>
    <Text style={styles.identityLabel}>{label}</Text>
    <Text style={styles.identityColon}>:</Text>
    <Text style={styles.identityValue}>{formatText(value)}</Text>
  </View>
);

export const InnoPayslipPage = ({ payslipData }) => {
  if (!payslipData) {
    return null;
  }

  const earnings = [
    ...pickSection(payslipData?.earnings),
    ...pickSection(payslipData?.other_earnings),
  ];
  const deductions = [
    ...pickSection(payslipData?.deduction, payslipData?.deductions),
    ...pickSection(
      payslipData?.other_deduction,
      payslipData?.other_deductions,
      payslipData?.additional_deduction,
    ),
  ];

  // get-payslip-details returns the fixed structure as its own list
  // (salary_structure_earnings), not a per-row field on `earnings`, so it's
  // matched back onto each earning row by component name.
  const fixedByComponent = Object.fromEntries(
    pickSection(payslipData?.salary_structure_earnings).map((item) => [
      normalizeKey(getName(item)),
      getAmount(item),
    ]),
  );

  const earnedLeave = findLeaveType(payslipData?.leave_balance_details, "earned");
  // "casu" (not "casual") also matches the backend's "Casuval leave" typo.
  const casualLeave = findLeaveType(payslipData?.leave_balance_details, "casu");
  const getFixedAmount = (item) => fixedByComponent[normalizeKey(getName(item))];

  const fixedTotal = sumAmounts(
    earnings.map((item) => ({ amount: getFixedAmount(item) })),
  );
  const earningTotal =
    toNumber(payslipData?.total_earnings) +
    toNumber(payslipData?.total_other_earnings);
  const deductionTotal =
    toNumber(payslipData?.total_deductions) +
    toNumber(payslipData?.total_other_deductions ?? payslipData?.total_additional_deductions);

  const payPeriod = formatText(payslipData?.pay_period ?? payslipData?.pay_schedule_name);

  return (
    <Page size="A4" style={styles.page}>
      <View>
        <Text style={[styles.title, styles.bold]}>
          {formatText(payslipData?.company_name).toUpperCase()}
        </Text>
        <Text style={[styles.center, styles.bold]}>
          {formatText(payslipData?.company_address)}
        </Text>
        <Text style={[styles.title, { fontSize: 9.5 }]}>
          Pay Slip for the month of {payPeriod.toUpperCase()}
        </Text>
      </View>

      <View style={styles.identity}>
        <View style={styles.identityColumn}>
          <IdentityLine label="Emp. No" value={payslipData?.employee_id} />
          <IdentityLine label="Employee Name" value={payslipData?.user_name} />
          <IdentityLine label="DOJ" value={formatDate(payslipData?.date_of_join)} />
          <IdentityLine label="Sal. Cal. Day" value={payslipData?.total_payable_shift} />
          <IdentityLine label="Pay Day" value={getPayableDays(payslipData)} />
          <IdentityLine label="Present Day" value={getPayableDays(payslipData)} />
          <IdentityLine label="LOP" value={payslipData?.lop_days} />
        </View>
        <View style={styles.identityColumn}>
          <IdentityLine label="Department" value={payslipData?.department_name} />
          <IdentityLine label="Designation" value={payslipData?.user_designation} />
          <IdentityLine label="Categories" value={payslipData?.employee_group_name} />
          <IdentityLine label="ESI No" value={payslipData?.esi_no} />
          <IdentityLine label="PF No" value={payslipData?.pf_no} />
          <IdentityLine label="UAN No" value={payslipData?.uan_no} />
          <IdentityLine label="A/C No" value={payslipData?.account_no} />
        </View>
      </View>

      <View style={styles.box}>
        <View style={styles.head}>
          <Text style={[styles.headCell, styles.colBorder, { width: COLS.component }]}>
            COMPONENTS
          </Text>
          <Text style={[styles.headCell, styles.colBorder, { width: COLS.fixed }]}>
            FIXED{"\n"}SALARY
          </Text>
          <Text style={[styles.headCell, styles.colBorder, { width: COLS.earning }]}>
            EARNING{"\n"}SALARY
          </Text>
          <Text style={[styles.headCell, styles.colBorder, { width: COLS.deductionName }]}>
            Deductions
          </Text>
          <Text style={[styles.headCell, { width: COLS.deductionAmount }]}>Amount</Text>
        </View>

        {/* Each column stretches to the full body height so its divider runs the whole table. */}
        <View style={styles.body}>
          <View style={[styles.column, styles.colBorder, { width: COLS.component }]}>
            {earnings.map((item, index) => (
              <Text key={index} style={styles.cell}>{getName(item)}</Text>
            ))}
          </View>
          <View style={[styles.column, styles.colBorder, { width: COLS.fixed }]}>
            {earnings.map((item, index) => (
              <Text key={index} style={styles.cellRight}>
                {formatMoney(getFixedAmount(item) ?? 0)}
              </Text>
            ))}
          </View>
          <View style={[styles.column, styles.colBorder, { width: COLS.earning }]}>
            {earnings.map((item, index) => (
              <Text key={index} style={styles.cellRight}>
                {formatMoney(getAmount(item))}
              </Text>
            ))}
          </View>
          <View style={[styles.column, styles.colBorder, { width: COLS.deductionName }]}>
            {deductions.map((item, index) => (
              <Text key={index} style={styles.cell}>{getName(item)}</Text>
            ))}
          </View>
          <View style={[styles.column, { width: COLS.deductionAmount }]}>
            {deductions.map((item, index) => (
              <Text key={index} style={styles.cellCenter}>
                {formatMoney(getAmount(item))}
              </Text>
            ))}
          </View>
        </View>

        <View style={styles.totalRow}>
          <Text style={[styles.cell, styles.colBorder, { width: COLS.component }]}>Total</Text>
          <Text style={[styles.cellRight, styles.colBorder, { width: COLS.fixed }]}>
            {formatMoney(fixedTotal)}
          </Text>
          <Text style={[styles.cellRight, styles.colBorder, { width: COLS.earning }]}>
            {formatMoney(earningTotal)}
          </Text>
          <Text style={[styles.cell, styles.colBorder, { width: COLS.deductionName }]}>Total</Text>
          <Text style={[styles.cellCenter, { width: COLS.deductionAmount }]}>
            {formatMoney(deductionTotal)}
          </Text>
        </View>

        <View style={styles.netRow}>
          <Text>
            Net Pay : {formatMoney(payslipData?.net_pay ?? payslipData?.net_salary)}
            {"    "}In Words : {formatText(payslipData?.net_pay_in_words)}
          </Text>
        </View>
        <View style={styles.signatureRow}>
          <Text style={styles.bold}>Signature</Text>
          <Text style={styles.bold}>
            For {formatText(payslipData?.company_name).toUpperCase()}
          </Text>
        </View>
      </View>

      <View style={styles.leaveBox}>
        <Text>
          TOTAL EARNED LEAVE: {leaveValue(earnedLeave, "credited_quantity", "total_leave", "total")}
          {"    "}UTILIZED EARNED LEAVE : {leaveValue(earnedLeave, "taken_quantity", "utilized_leave", "utilized")}
          {"    "}BALANCE EARNED LEAVE: {leaveValue(earnedLeave, "remaining_quantity", "balance_leave", "balance")}
        </Text>
        <Text>
          TOTAL CASUAL LEAVE: {leaveValue(casualLeave, "credited_quantity", "total_leave", "total")}
          {"    "}UTILIZED CASUAL LEAVE : {leaveValue(casualLeave, "taken_quantity", "utilized_leave", "utilized")}
          {"    "}BALANCE CASUAL LEAVE: {leaveValue(casualLeave, "remaining_quantity", "balance_leave", "balance")}
        </Text>
      </View>
    </Page>
  );
};

const InnoPayslipData = ({ payslipData }) => (
  <Document
    title={`${
      payslipData?.user_name ? payslipData?.user_name + " " : ""
    }${payslipData?.pay_schedule_name || "payslip"}`}
  >
    <InnoPayslipPage payslipData={payslipData} />
  </Document>
);

export const BulkInnoPayslipData = ({ employeesPdfData = [] }) => (
  <Document>
    {employeesPdfData
      .filter((emp) => emp?.payslipData)
      .map((employee, index) => (
        <InnoPayslipPage key={index} payslipData={employee.payslipData} />
      ))}
  </Document>
);

export default InnoPayslipData;
